import styled from '@emotion/styled'
import { faBars, faPowerOff, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useState } from 'react'
import ReactFlagsSelect from 'react-flags-select'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Breakpoint from '../../../Breakpoint'
import images from '../../../assets/img/images'
import { useMenu } from '../../../context/MenuContext'
import { useLangSelected } from '../../../hooks/useLangSelected'
import { MainHeaderNav } from './MainNav'

import { clearTokenByRole, clearTokens } from '../../../utils/tokenManager'

export const disconnect = async (lang: string = 'fr', currentPath: string = '') => {
    try {
        // Détecter l'espace actuel basé sur le chemin
        let role: 'Admin' | 'Member' | 'Coach' | null = null

        if (currentPath.includes('/bva-admin')) {
            role = 'Admin'
        } else if (currentPath.includes('/bva-membre')) {
            role = 'Member'
        } else if (currentPath.includes('/bva-coach')) {
            role = 'Coach'
        }

        if (role) {
            // Déconnexion sélective de l'espace actuel
            await clearTokenByRole(role)
        } else {
            // Déconnexion complète si aucun espace détecté
            await clearTokens()
        }
    } catch (error) {
        console.error('Error during disconnect:', error)
    } finally {
        window.location.href = `/${lang}/login`
    }
}

const Header = () => {
    const location = useLocation()
    const langSelected = useLangSelected()
    const [selectLng, setSelectLng] = useState(langSelected.toUpperCase())
    const { t, i18n } = useTranslation('translation')
    const [selectedLngLowerCase, setSelectedLngLowerCase] = useState(langSelected)
    const [prevScrollPos, setPrevScrollPos] = useState(0)
    const [visible, setVisible] = useState(true)
    const navigate = useNavigate()
    const { isMenuOpen, toggleMenu } = useMenu()

    // Vérifier si on est sur une page qui nécessite le bouton burger
    const showBurgerButton = ['/bva-admin', '/bva-coach', '/bva-membre'].some((path) =>
        location.pathname.includes(path),
    )

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollPos = window.pageYOffset
            const isScrollingDown = prevScrollPos < currentScrollPos && currentScrollPos > 60

            setVisible(!isScrollingDown)
            setPrevScrollPos(currentScrollPos)
        }

        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [prevScrollPos, visible])

    return (
        <HeaderCustom isVisible={visible}>
            {showBurgerButton && (
                <BurgerButton onClick={toggleMenu}>
                    <FontAwesomeIcon icon={isMenuOpen ? faTimes : faBars} />
                </BurgerButton>
            )}

            <LogoLink to={`/${selectedLngLowerCase}/`}>
                <img src={images.logoBVA} alt="Logo du BVA" />
            </LogoLink>

            <ReactFlagsSelectCustom
                selected={selectLng}
                customLabels={{
                    FR: t('global.lang.fr'),
                    GB: t('global.lang.gb'),
                    BR: t('global.lang.br'),
                }}
                onSelect={(code) => {
                    setSelectLng(code)
                    setSelectedLngLowerCase(code.toLowerCase())

                    const pathnameUpdated = location.pathname
                        .split('/')
                        .filter((x, i) => i !== 1)
                        .join('/')

                    i18n.changeLanguage(code.toLowerCase()).then(() => {
                        navigate(`/${code.toLowerCase()}${pathnameUpdated}`, { replace: true })
                    })
                }}
                countries={['FR', 'GB', 'BR']}
                showSelectedLabel={false}
            />

            <HeaderRight>
                {location.pathname.includes('/bva-admin') ||
                location.pathname.includes('/bva-membre') ||
                location.pathname.includes('/bva-coach') ? (
                    <DisconnectButton>
                        <FontAwesomeIcon
                            icon={faPowerOff}
                            onClick={() => disconnect(selectedLngLowerCase, location.pathname)}
                        />
                    </DisconnectButton>
                ) : (
                    <MainHeaderNav selectedLngLowerCase={selectedLngLowerCase} />
                )}
            </HeaderRight>
        </HeaderCustom>
    )
}

const ReactFlagsSelectCustom = styled(ReactFlagsSelect)`
    height: min-content;
    align-self: center;
    display: flex;
    position: relative;
    align-items: center;

    & ul {
        width: 200px;
        top: 27px;
    }
`

const DisconnectButton = styled.div`
    display: flex;
    align-items: center;
    margin-right: 24px;
    margin-left: 8px;

    & .fa-power-off {
        height: 50px;
        width: 50px;
        color: var(--primary-color);
        cursor: pointer;
    }

    &:active {
        transform: scale(0.95);
    }

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        & .fa-power-off {
            width: 40px;
            height: 40px;
        }
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        margin-right: 8px;
        margin-left: 0p;
    }
`

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    width: 20%;
`

const HeaderRight = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 80%;

    @media (max-width: ${Breakpoint.TABLET_L}) {
        justify-content: flex-end;
        width: min-content;
    }
`

const BurgerButton = styled.button`
    background: none;
    border: none;
    color: var(--primary-color);
    font-size: 1.5rem;
    cursor: pointer;
    display: none;
    padding: 0.5rem;
    margin-left: 10px;

    & svg {
        width: 3rem;
        height: 3rem;
    }

    @media (max-width: 991px) {
        display: block;
    }
`

const HeaderCustom = styled.header<{ isVisible: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 5%;
    background-color: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    position: fixed;
    width: 100%;
    top: 0;
    z-index: 1000;
    transition: transform 0.3s ease-in-out;
    transform: translateY(${(props) => (props.isVisible ? '0' : '-100%')});

    @media (min-width: 992px) {
        transform: translateY(0) !important;
    }
`

const LogoLink = styled(Link)`
    display: flex;
    align-items: center;
    width: 100px;
    margin-left: 2%;
    margin-right: 8px;

    & span {
        display: flex;
        align-items: center;
    }

    & img {
        width: 100px;
        height: 100px;
    }

    &:active {
        transform: scale(0.95);
    }

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        & img {
            width: 70px;
            height: 70px;
        }
    }
`

export default Header
